import { Icon } from "@iconify/react";
import { useCallback, useMemo, useState } from "react";
import { useTicketApi } from "@/api/tickets";
import { Loader } from "@/components/motion/loader";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { groupTicketActivities } from "../utils/ticket-activity-groups";
import { attachmentIcon, attachmentTypeLabel, formatFileSize, isPreviewableAttachment, type TicketAttachmentView } from "../utils/ticket-attachments";
import { findTicketMentionTokenMatches } from "../utils/ticket-mentions";
import type { TicketActivityItem } from "../utils/ticket-presentation";
import { AgentMentionHoverCard } from "./agent-mention-hover-card";
import { AttachmentPreviewDialog } from "./attachment-preview-dialog";
import { TicketActivityEntry } from "./ticket-activity-entry";
import { TicketEventActivity, TicketEventActivityGroup } from "./ticket-event-activity";

function renderMessageContent(content: string, mentions: Extract<TicketActivityItem, { kind: "note" }>["mentions"], ticketId: string) {
  const mentionById = new Map((mentions ?? []).map((mention) => [mention.agentId, mention]));
  const matches = findTicketMentionTokenMatches(content);
  if (matches.length === 0) return content;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) parts.push(content.slice(cursor, match.start));
    const mention = mentionById.get(match.agentId);
    parts.push(
      mention ? (
        <AgentMentionHoverCard key={`${mention.agentId}-${match.start}`} mention={mention} ticketId={ticketId}>
          @{mention.displayName}
        </AgentMentionHoverCard>
      ) : (
        content.slice(match.start, match.end)
      ),
    );
    cursor = match.end;
  }
  if (cursor < content.length) parts.push(content.slice(cursor));
  return parts;
}

export function TicketActivity({ activities, ticketId }: { activities: TicketActivityItem[]; ticketId: string }) {
  const { intlLocale, t } = useI18n();
  const [previewAttachment, setPreviewAttachment] = useState<TicketAttachmentView | null>(null);
  const ticketApi = useTicketApi();
  const rows = useMemo(() => groupTicketActivities(activities), [activities]);

  // 预览对话框把它放进 effect 依赖里，引用必须稳定，否则每次渲染都会重新拉一遍附件。
  const loadAttachmentContent = useCallback(
    (attachment: TicketAttachmentView) => {
      if (!attachment.remote) return Promise.reject(new Error(t("tickets.attachment.noRemoteSource")));
      return ticketApi.downloadAttachment(attachment.remote.ticketId, attachment.remote.attachmentId);
    },
    [t, ticketApi],
  );

  return (
    <>
      <section aria-label={t("tickets.activity.timelineAria")} className="mx-auto w-full max-w-3xl">
        <div className="flex flex-col gap-1 py-2">
          {rows.map((activity) => {
            if (activity.kind === "event-group") {
              return <TicketEventActivityGroup group={activity} key={`${ticketId}:${activity.id}`} />;
            }
            if (activity.kind === "event") {
              return <TicketEventActivity activity={activity} key={activity.id} />;
            }

            const isSending = activity.deliveryState === "sending";
            const deliveryStatus =
              activity.deliveryState === "sent"
                ? { icon: "tabler:circle-check", label: t("tickets.activity.sent"), className: "text-success" }
                : activity.deliveryState === "failed"
                  ? { icon: "tabler:alert-circle", label: t("tickets.activity.failed"), className: "text-destructive" }
                  : null;

            return (
              <TicketActivityEntry
                author={activity.author}
                avatarUrl={activity.avatarUrl}
                busy={isSending}
                initials={activity.initials}
                key={activity.id}
                meta={
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon className="size-3.5" icon="tabler:lock" />
                    {t("tickets.activity.internalMessage")}
                  </span>
                }
                time={activity.time}
              >
                {activity.content ? (
                  <p className="mt-1 max-w-2xl whitespace-pre-wrap text-sm leading-6 text-foreground">
                    {renderMessageContent(activity.content, activity.mentions, ticketId)}
                  </p>
                ) : null}

                {activity.attachments && activity.attachments.length > 0 ? (
                  <ul aria-label={t("tickets.attachment.messageListAria")} className="mt-1.5 flex flex-col items-start gap-0.5">
                    {activity.attachments.map((attachment) => {
                      // 发送中的条目还只有本地文件，能预览但还没有可下载的服务端地址。
                      const canOpen = isPreviewableAttachment(attachment) || attachment.remote !== null;
                      return (
                        <li className="max-w-full min-w-0" key={attachment.key}>
                          <button
                            aria-label={canOpen ? t("tickets.attachment.openAria", { name: attachment.name }) : attachment.name}
                            className="group -ml-1 flex w-fit max-w-full items-center gap-2 rounded-md px-1 py-1 text-left transition-colors enabled:hover:bg-background enabled:focus-visible:outline-none enabled:focus-visible:ring-2 enabled:focus-visible:ring-ring disabled:cursor-default"
                            disabled={!canOpen}
                            type="button"
                            onClick={() => setPreviewAttachment(attachment)}
                          >
                            <Icon className="size-4 shrink-0" icon={attachmentIcon(attachment)} />
                            <span className="min-w-0 max-w-md shrink truncate text-sm text-foreground/80 sm:shrink-0">{attachment.name}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {attachmentTypeLabel(attachment, t)} · {formatFileSize(attachment.size, intlLocale)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}

                {isSending ? (
                  <p aria-live="polite" className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Loader className="text-current" label={t("tickets.activity.sendingInternalMessage")} size={12} />
                    {t("tickets.activity.sending")}
                  </p>
                ) : deliveryStatus ? (
                  <p aria-live="polite" className={cn("mt-1.5 inline-flex items-center gap-1 text-[11px]", deliveryStatus.className)}>
                    <Icon className="size-3" icon={deliveryStatus.icon} />
                    {deliveryStatus.label}
                  </p>
                ) : null}
              </TicketActivityEntry>
            );
          })}
        </div>
      </section>
      <AttachmentPreviewDialog attachment={previewAttachment} loadContent={loadAttachmentContent} onClose={() => setPreviewAttachment(null)} />
    </>
  );
}
